import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Button, Loading, Search, RadioButton } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { api } from 'lib/request/api';
import { useInfiniteScroll } from 'lib/hooks/useInfiniteScroll';
import { parseUTCdateTime } from 'utils/dates';
import type { TypeCurveWorkspaceResponse } from 'types/type-curve-workspace';

import { tcAreaPreviewAtom, tcDetailsRightPanelAtom } from './atoms';
import { tcTasksAtom } from './tasks';

import style from './tc-area-selector-panel.module.scss';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TypeCurveAreaResult {
  id: number;
  uuid: string;
  name: string;
  active: boolean;
  modifiedBy: string | null;
  modifiedDate: string | null;
  createdAt: string;
  updatedAt: string;
  state: string | null;
  href: string;
  auth: string[];
}

interface TypeCurveAreaQueryResponse {
  totalCount: number;
  page?: number;
  pageSize?: number;
  results: TypeCurveAreaResult[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TcAreaSelectorPanelProps {
  workspace: TypeCurveWorkspaceResponse;
  currentTypeCurveAreaId?: number | null;
  onSaveSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Reducer (adapted from ListDrawer pattern)
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;

type Phase = 'idle' | 'loading-first' | 'has-data' | 'error-first';
type LoadMoreStatus = 'idle' | 'in-flight' | 'error';

interface State {
  accumulated: TypeCurveAreaResult[];
  totalCount: number | null;
  currentPage: number;
  phase: Phase;
  loadMoreStatus: LoadMoreStatus;
}

type Action =
  | { type: 'fetchFirstStart' }
  | {
      type: 'fetchFirstSuccess';
      results: TypeCurveAreaResult[];
      totalCount: number;
    }
  | { type: 'fetchFirstError' }
  | { type: 'loadMoreStart' }
  | {
      type: 'loadMoreSuccess';
      page: number;
      results: TypeCurveAreaResult[];
      totalCount: number;
    }
  | { type: 'loadMoreError' };

function reducer(current: State, action: Action): State {
  switch (action.type) {
    case 'fetchFirstStart':
      return {
        ...current,
        phase: 'loading-first',
        accumulated: [],
        currentPage: 1,
        loadMoreStatus: 'idle',
      };
    case 'fetchFirstSuccess':
      return {
        ...current,
        phase: 'has-data',
        accumulated: action.results,
        totalCount: action.totalCount,
        currentPage: 1,
        loadMoreStatus: 'idle',
      };
    case 'fetchFirstError':
      return { ...current, phase: 'error-first' };
    case 'loadMoreStart':
      return { ...current, loadMoreStatus: 'in-flight' };
    case 'loadMoreSuccess':
      return {
        ...current,
        accumulated: [...current.accumulated, ...action.results],
        totalCount: action.totalCount,
        currentPage: action.page,
        loadMoreStatus: 'idle',
      };
    case 'loadMoreError':
      return { ...current, loadMoreStatus: 'error' };
  }
}

const INITIAL_STATE: State = {
  accumulated: [],
  totalCount: null,
  currentPage: 1,
  phase: 'idle',
  loadMoreStatus: 'idle',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TcAreaSelectorPanel = ({
  workspace,
  currentTypeCurveAreaId,
  onSaveSuccess,
}: TcAreaSelectorPanelProps) => {
  const setRightPanel = useSetAtom(tcDetailsRightPanelAtom);
  const setTasks = useSetAtom(tcTasksAtom);
  const setPreview = useSetAtom(tcAreaPreviewAtom);
  const fetchControllerRef = useRef<AbortController | null>(null);

  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const generationRef = useRef(0);

  const [searchText, setSearchText] = useState('');
  // Latest search text, read by the fetch callbacks so they can send `text`
  // without changing identity on every keystroke.
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  // Three-way: undefined = nothing selected yet | null = "None" | number = area id
  const [selectedAreaId, setSelectedAreaId] = useState<
    number | null | undefined
  >(() => {
    return currentTypeCurveAreaId !== undefined
      ? currentTypeCurveAreaId
      : undefined;
  });
  const [saving, setSaving] = useState(false);

  // Cleanup preview and abort in-flight fetch on unmount
  useEffect(() => {
    return () => {
      fetchControllerRef.current?.abort();
      setPreview(null);
    };
  }, [setPreview]);

  // ---------------------------------------------------------------------------
  // Fetch helpers
  // ---------------------------------------------------------------------------

  const runFirstFetch = useCallback(async () => {
    const requestGen = ++generationRef.current;
    const text = searchTextRef.current.trim();
    dispatch({ type: 'fetchFirstStart' });
    try {
      const response = await api
        .post('queries/type_curve_areas', {
          json: {
            sortAttribute: 'name',
            sortDirection: 'ASC',
            page: 1,
            pageSize: PAGE_SIZE,
            paginated: true,
            ...(text ? { text } : {}),
          },
        })
        .json<TypeCurveAreaQueryResponse>();
      if (requestGen !== generationRef.current) return;
      dispatch({
        type: 'fetchFirstSuccess',
        results: response.results,
        totalCount: response.totalCount,
      });
    } catch {
      if (requestGen !== generationRef.current) return;
      dispatch({ type: 'fetchFirstError' });
    }
  }, []);

  const runLoadMore = useCallback(async () => {
    if (state.loadMoreStatus === 'in-flight') return;
    const nextPage = state.currentPage + 1;
    const requestGen = ++generationRef.current;
    const text = searchTextRef.current.trim();
    dispatch({ type: 'loadMoreStart' });
    try {
      const response = await api
        .post('queries/type_curve_areas', {
          json: {
            sortAttribute: 'name',
            sortDirection: 'ASC',
            page: nextPage,
            pageSize: PAGE_SIZE,
            paginated: true,
            ...(text ? { text } : {}),
          },
        })
        .json<TypeCurveAreaQueryResponse>();
      if (requestGen !== generationRef.current) return;
      dispatch({
        type: 'loadMoreSuccess',
        page: nextPage,
        results: response.results,
        totalCount: response.totalCount,
      });
    } catch {
      if (requestGen !== generationRef.current) return;
      dispatch({ type: 'loadMoreError' });
    }
  }, [state.currentPage, state.loadMoreStatus]);

  // ---------------------------------------------------------------------------
  // Backend text search — refetch (page 1) with `text` as the user types.
  // Search is server-side; results come back already filtered.
  // ---------------------------------------------------------------------------

  const debouncedRefetch = useMemo(
    () => debounce(() => runFirstFetch(), 400),
    [runFirstFetch]
  );
  useEffect(() => () => debouncedRefetch.cancel(), [debouncedRefetch]);

  // First load runs immediately; subsequent search-text changes are debounced.
  const didInitRef = useRef(false);
  useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
      runFirstFetch();
      return;
    }
    debouncedRefetch();
  }, [searchText, runFirstFetch, debouncedRefetch]);

  // ---------------------------------------------------------------------------
  // Infinite scroll
  // ---------------------------------------------------------------------------

  const hasMore =
    state.totalCount !== null && state.accumulated.length < state.totalCount;
  // Search is server-side now, so pagination is valid over the filtered result
  // set — infinite scroll keeps loading further pages (carrying the same `text`).
  const canAutoLoadMore = hasMore && state.loadMoreStatus !== 'error';

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: runLoadMore,
    hasMore: canAutoLoadMore,
    loading: state.loadMoreStatus === 'in-flight',
  });

  // ---------------------------------------------------------------------------
  // Selection helpers
  // ---------------------------------------------------------------------------

  const handleSelectArea = useCallback(
    async (areaId: number | null) => {
      setSelectedAreaId(areaId);

      // Abort any in-flight preview fetch
      fetchControllerRef.current?.abort();

      if (areaId === null) {
        setPreview(null);
        return;
      }

      const controller = new AbortController();
      fetchControllerRef.current = controller;
      setPreview({ areaId, geometry: null, loading: true });

      try {
        const response = await api
          .get(`type_curve_areas/${areaId}`, { signal: controller.signal })
          .json<{ geometry: GeoJSON.Geometry | null }>();
        if (controller.signal.aborted) return;
        setPreview({ areaId, geometry: response.geometry, loading: false });
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setPreview(null);
      }
    },
    [setPreview]
  );

  const selectionChanged =
    selectedAreaId !== undefined && selectedAreaId !== currentTypeCurveAreaId;

  const close = useCallback(() => {
    setPreview(null);
    setRightPanel({ type: 'dca', open: false });
  }, [setPreview, setRightPanel]);

  const handleSave = useCallback(async () => {
    if (selectedAreaId === undefined) return;

    setSaving(true);
    try {
      await api
        .patch(`type_curve_workspaces/${workspace.id}/header`, {
          json: { header: { type_curve_area_id: selectedAreaId } },
        })
        .json();

      // Auto-mark verify-tc-area task as correct when an area is selected
      if (selectedAreaId !== null) {
        setTasks((prev) =>
          prev.map((t) => {
            return t.id === 'verify-tc-area'
              ? {
                  ...t,
                  status: 'correct' as const,
                  updatedAt: new Date().toISOString(),
                }
              : t;
          })
        );
      }
      setPreview(null);
      onSaveSuccess?.();
      close();
    } finally {
      setSaving(false);
    }
  }, [
    selectedAreaId,
    workspace.id,
    setTasks,
    setPreview,
    onSaveSuccess,
    close,
  ]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={style.root}>
      {/* Header */}
      <div className={style.panelHeader}>
        <div className={style.headerCopy}>
          <h2 className={style.title}>Type Curve Areas</h2>
          <p className={style.subtitle}>
            Select the area of interest for this workspace.
          </p>
        </div>
        <Button
          hasIconOnly
          size="lg"
          kind="ghost"
          onClick={close}
          iconDescription="Close"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={(iconProps) => <Close size={20} {...iconProps} />}
        />
      </div>

      {/* Search */}
      <div className={style.searchRow}>
        <Search
          id="tc-area-search"
          labelText="Search areas"
          placeholder="Search areas..."
          value={searchText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchText(e.target.value)
          }
          size="md"
        />
      </div>

      {/* Scrollable body */}
      <div className={style.scrollBody}>
        {state.phase === 'loading-first' && (
          <div className={style.loadingContainer}>
            <Loading description="Loading" withOverlay={false} small />
          </div>
        )}

        {state.phase === 'error-first' && (
          <div className={style.loadMoreError}>
            <span>Failed to load areas.</span>
            <Button kind="ghost" size="sm" onClick={runFirstFetch}>
              Try again
            </Button>
          </div>
        )}

        {state.phase === 'has-data' && (
          <>
            {/* "None" option — always visible regardless of search */}
            <div
              className={style.noneOption}
              onClick={() => handleSelectArea(null)}
            >
              <RadioButton
                id="tc-area-none"
                labelText=""
                hideLabel
                name="tc-area-selector"
                value="none"
                checked={selectedAreaId === null}
                onClick={() => handleSelectArea(null)}
              />
              <div className={style.areaInfo}>
                <span className={style.areaName}>None</span>
                <p className={style.areaMeta}>No Type Curve Area selected</p>
                <p className={style.areaDates}>
                  Wells and units stay hidden until an area is configured.
                </p>
              </div>
            </div>

            {state.accumulated.length === 0 && searchText.trim() ? (
              <p className={style.emptyText}>No areas match your search.</p>
            ) : (
              state.accumulated.map((area) => (
                <div
                  key={area.id}
                  className={style.areaRow}
                  onClick={() => handleSelectArea(area.id)}
                >
                  <RadioButton
                    id={`tc-area-${area.id}`}
                    labelText=""
                    hideLabel
                    name="tc-area-selector"
                    value={String(area.id)}
                    checked={selectedAreaId === area.id}
                    onClick={() => handleSelectArea(area.id)}
                  />
                  <div className={style.areaInfo}>
                    <span className={style.areaName}>{area.name}</span>
                    {area.modifiedBy && (
                      <p className={style.areaMeta}>
                        Modified by {area.modifiedBy}
                      </p>
                    )}
                    <p className={style.areaDates}>
                      Created: {parseUTCdateTime(area.createdAt)}
                      {area.updatedAt
                        ? ` | Updated: ${parseUTCdateTime(area.updatedAt)}`
                        : ''}
                    </p>
                  </div>
                </div>
              ))
            )}

            <div ref={sentinelRef} className={style.sentinel} />

            {state.loadMoreStatus === 'in-flight' && (
              <div className={style.loadingMore}>
                <Loading
                  description="Loading more..."
                  withOverlay={false}
                  small
                />
              </div>
            )}

            {state.loadMoreStatus === 'error' && (
              <div className={style.loadMoreError}>
                <span>Failed to load more areas.</span>
                <Button kind="ghost" size="sm" onClick={runLoadMore}>
                  Try again
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className={style.footer}>
        <Button kind="secondary" size="lg" onClick={close} disabled={saving}>
          Cancel
        </Button>
        <Button
          kind="primary"
          size="lg"
          onClick={handleSave}
          disabled={!selectionChanged || saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};
